import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PaperClipIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AdminPurchaseInvoices = () => {
  const { user, dashboardUser, getToken } = useAuth();
  const currentUser = dashboardUser || user;
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    supplier_id: '',
    payment_status: '',
    status: ''
  });
  const [formData, setFormData] = useState({
    supplier_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    supplier_invoice_no: '',
    received_date: '',
    due_date: '',
    items: [],
    other_charges: '0',
    discount_total: '0',
    notes: ''
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [newItem, setNewItem] = useState({
    product_id: '',
    qty: '1',
    unit_cost: '0',
    tax_percent: '0'
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'cash',
    txn_reference: '',
    notes: ''
  });

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken(true);
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch purchase invoices');
      }

      const data = await response.json();
      if (data.success) {
        setInvoices(data.data || []);
        setTotalPages(Math.ceil((data.pagination?.total || 0) / 10));
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(error.message || 'Failed to load purchase invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, currentPage, filters, getToken, navigate]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/suppliers?status=active`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSuppliers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [API_BASE, getToken]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/products?limit=1000`);
      const data = await response.json();
      if (data.success) {
        setProducts(data.products || data.data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'employee')) {
      fetchInvoices();
      fetchSuppliers();
      fetchProducts();
    } else if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, fetchInvoices, fetchProducts, fetchSuppliers, navigate]);

  const fetchInvoiceDetails = async (id) => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedInvoice(data.data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to load invoice details');
    }
  };

  const openAddModal = () => {
    setEditingInvoice(null);
    setFormData({
      supplier_id: '',
      invoice_date: new Date().toISOString().split('T')[0],
      supplier_invoice_no: '',
      received_date: '',
      due_date: '',
      items: [],
      other_charges: '0',
      discount_total: '0',
      notes: ''
    });
    setInvoiceItems([]);
    setNewItem({ product_id: '', qty: '1', unit_cost: '0', tax_percent: '0' });
    setShowForm(true);
  };

  const openEditModal = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      supplier_id: invoice.supplier_id,
      invoice_date: invoice.invoice_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      supplier_invoice_no: invoice.supplier_invoice_no || '',
      received_date: invoice.received_date?.split('T')[0] || '',
      due_date: invoice.due_date?.split('T')[0] || '',
      other_charges: invoice.other_charges || '0',
      discount_total: invoice.discount_total || '0',
      notes: invoice.notes || ''
    });
    // Fetch invoice items
    fetchInvoiceItemsForEdit(invoice.id);
    setShowForm(true);
  };

  const fetchInvoiceItemsForEdit = async (invoiceId) => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${invoiceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success && data.data.items) {
        setInvoiceItems(data.data.items.map(item => ({
          product_id: item.product_id,
          qty: item.qty,
          unit_cost: item.unit_cost,
          tax_percent: item.tax_percent || '0'
        })));
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    if (!newItem.product_id || !newItem.qty || !newItem.unit_cost) {
      toast.error('Please fill all item fields');
      return;
    }
    setInvoiceItems(prev => [...prev, { ...newItem }]);
    setNewItem({ product_id: '', qty: '1', unit_cost: '0', tax_percent: '0' });
  };

  const removeItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    invoiceItems.forEach(item => {
      const lineTotal = parseFloat(item.qty) * parseFloat(item.unit_cost);
      const taxAmount = lineTotal * (parseFloat(item.tax_percent || 0) / 100);
      subtotal += lineTotal;
      taxTotal += taxAmount;
    });
    const otherCharges = parseFloat(formData.other_charges || 0);
    const discount = parseFloat(formData.discount_total || 0);
    const grandTotal = subtotal + taxTotal + otherCharges - discount;
    return { subtotal, taxTotal, otherCharges, discount, grandTotal };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id || !formData.invoice_date || invoiceItems.length === 0) {
      toast.error('Please fill all required fields and add at least one item');
      return;
    }

    setLoading(true);
    try {
      const token = getToken(true);
      const method = editingInvoice ? 'PUT' : 'POST';
      const url = editingInvoice
        ? `${API_BASE}/api/purchases/purchase-invoices/${editingInvoice.id}`
        : `${API_BASE}/api/purchases/purchase-invoices`;

      const payload = {
        ...formData,
        items: invoiceItems.map(item => ({
          product_id: item.product_id,
          qty: parseFloat(item.qty),
          unit_cost: parseFloat(item.unit_cost),
          tax_percent: parseFloat(item.tax_percent || 0)
        }))
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editingInvoice ? 'update' : 'create'} invoice`);
      }

      toast.success(`Invoice ${editingInvoice ? 'updated' : 'created'} successfully!`);
      setShowForm(false);
      fetchInvoices();
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error(error.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (id) => {
    if (!window.confirm('Post this invoice? This will update stock quantities and cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${id}/post`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to post invoice');
      }

      toast.success('Invoice posted successfully! Stock updated.');
      fetchInvoices();
    } catch (error) {
      console.error('Error posting invoice:', error);
      toast.error(error.message || 'Failed to post invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete invoice');
      }

      toast.success('Invoice deleted successfully!');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error(error.message || 'Failed to delete invoice');
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: (parseFloat(invoice.grand_total) - parseFloat(invoice.paid_amount || 0)).toString(),
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'cash',
      txn_reference: '',
      notes: ''
    });
    setShowPayment(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || !paymentData.payment_date || !paymentData.payment_mode) {
      toast.error('Please fill all required payment fields');
      return;
    }

    setLoading(true);
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${selectedInvoice.id}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add payment');
      }

      toast.success('Payment recorded successfully!');
      setShowPayment(false);
      fetchInvoices();
      if (showDetails) {
        fetchInvoiceDetails(selectedInvoice.id);
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      toast.error(error.message || 'Failed to add payment');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, invoiceId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('invoice', file);

    setLoading(true);
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/purchases/purchase-invoices/${invoiceId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload file');
      }

      toast.success('Invoice file uploaded successfully!');
      fetchInvoices();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const totals = calculateTotals();

  if (loading && invoices.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading purchase invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900 sm:text-3xl">
          <DocumentTextIcon className="w-8 h-8 text-indigo-600" />
          Purchase Invoices
        </h1>
        <button
          onClick={openAddModal}
          className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 lg:w-auto"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          New Invoice
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters(prev => ({ ...prev, from: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters(prev => ({ ...prev, to: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Supplier</label>
            <select
              value={filters.supplier_id}
              onChange={(e) => setFilters(prev => ({ ...prev, supplier_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Suppliers</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="posted">Posted</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Status</label>
            <select
              value={filters.payment_status}
              onChange={(e) => setFilters(prev => ({ ...prev, payment_status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="xl:hidden divide-y divide-gray-200">
          {invoices.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              No purchase invoices found.
            </div>
          ) : (
            invoices.map((invoice) => {
              const balance = parseFloat(invoice.grand_total) - parseFloat(invoice.paid_amount || 0);
              const paymentStatus = balance === 0 ? 'paid' : (parseFloat(invoice.paid_amount || 0) > 0 ? 'partial' : 'unpaid');

              return (
                <div key={invoice.id} className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">{invoice.purchase_invoice_no}</h2>
                      <p className="mt-1 text-sm text-gray-500">{invoice.supplier_name}</p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      invoice.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Invoice Date</p>
                      <p className="mt-1 font-medium text-gray-900">{formatDate(invoice.invoice_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Payment Status</p>
                      <span className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        paymentStatus === 'paid' ? 'bg-blue-100 text-blue-800' :
                        paymentStatus === 'partial' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {paymentStatus}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total</p>
                      <p className="mt-1 font-semibold text-gray-900">{formatPrice(invoice.grand_total)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Paid</p>
                      <p className="mt-1 font-medium text-gray-900">{formatPrice(invoice.paid_amount || 0)}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Balance</p>
                      <p className="mt-1 font-semibold text-gray-900">{formatPrice(balance)}</p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => fetchInvoiceDetails(invoice.id)}
                      className="flex-1 rounded-lg border border-indigo-200 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 sm:flex-none"
                    >
                      View Details
                    </button>
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => openEditModal(invoice)}
                        className="flex-1 rounded-lg border border-yellow-200 px-4 py-2 text-sm font-medium text-yellow-700 hover:bg-yellow-50 sm:flex-none"
                      >
                        Edit
                      </button>
                    )}
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => handlePost(invoice.id)}
                        className="flex-1 rounded-lg border border-green-200 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 sm:flex-none"
                      >
                        Post
                      </button>
                    )}
                    {invoice.status === 'posted' && balance > 0 && (
                      <button
                        onClick={() => openPaymentModal(invoice)}
                        className="flex-1 rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 sm:flex-none"
                      >
                        Add Payment
                      </button>
                    )}
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="flex-1 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 sm:flex-none"
                      >
                        Delete
                      </button>
                    )}
                    <label className="flex-1 cursor-pointer rounded-lg border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 sm:flex-none">
                      Upload File
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, invoice.id)}
                      />
                    </label>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-[860px] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No purchase invoices found.
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => {
                  const balance = parseFloat(invoice.grand_total) - parseFloat(invoice.paid_amount || 0);
                  const paymentStatus = balance === 0 ? 'paid' : (parseFloat(invoice.paid_amount || 0) > 0 ? 'partial' : 'unpaid');

                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.purchase_invoice_no}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.supplier_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.invoice_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatPrice(invoice.grand_total)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatPrice(invoice.paid_amount || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatPrice(balance)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status}
                        </span>
                        <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          paymentStatus === 'paid' ? 'bg-blue-100 text-blue-800' :
                          paymentStatus === 'partial' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {paymentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => fetchInvoiceDetails(invoice.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {invoice.status === 'draft' && (
                            <>
                              <button
                                onClick={() => openEditModal(invoice)}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(invoice.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}
                          {invoice.status === 'draft' && (
                            <button
                              onClick={() => handlePost(invoice.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Post Invoice"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                          {invoice.status === 'posted' && balance > 0 && (
                            <button
                              onClick={() => openPaymentModal(invoice)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Add Payment"
                            >
                              <CurrencyDollarIcon className="h-5 w-5" />
                            </button>
                          )}
                          <label className="cursor-pointer text-gray-600 hover:text-gray-900" title="Upload File">
                            <PaperClipIcon className="h-5 w-5" />
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileUpload(e, invoice.id)}
                            />
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Invoice Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full my-8">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingInvoice ? 'Edit Purchase Invoice' : 'New Purchase Invoice'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                  <select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date *</label>
                  <input
                    type="date"
                    name="invoice_date"
                    value={formData.invoice_date}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Invoice #</label>
                  <input
                    type="text"
                    name="supplier_invoice_no"
                    value={formData.supplier_invoice_no}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Received Date</label>
                  <input
                    type="date"
                    name="received_date"
                    value={formData.received_date}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Items</h3>
                <div className="border border-gray-200 rounded-lg p-4 space-y-4">
                  {/* Add Item Form */}
                  <div className="grid grid-cols-5 gap-2">
                    <select
                      name="product_id"
                      value={newItem.product_id}
                      onChange={handleItemChange}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select Product</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      name="qty"
                      value={newItem.qty}
                      onChange={handleItemChange}
                      placeholder="Qty"
                      min="1"
                      step="1"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      name="unit_cost"
                      value={newItem.unit_cost}
                      onChange={handleItemChange}
                      placeholder="Unit Cost"
                      min="0"
                      step="0.01"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <input
                      type="number"
                      name="tax_percent"
                      value={newItem.tax_percent}
                      onChange={handleItemChange}
                      placeholder="Tax %"
                      min="0"
                      step="0.01"
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      type="button"
                      onClick={addItem}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                    >
                      Add Item
                    </button>
                  </div>

                  {/* Items List */}
                  {invoiceItems.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-[720px] w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Unit Cost</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tax %</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Line Total</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {invoiceItems.map((item, index) => {
                            const product = products.find(p => String(p.id) === String(item.product_id));
                            const lineTotal = parseFloat(item.qty) * parseFloat(item.unit_cost);
                            const taxAmount = lineTotal * (parseFloat(item.tax_percent || 0) / 100);
                            const lineTotalWithTax = lineTotal + taxAmount;
                            return (
                              <tr key={index}>
                                <td className="px-3 py-2 text-sm">{product?.name || 'N/A'}</td>
                                <td className="px-3 py-2 text-sm">{item.qty}</td>
                                <td className="px-3 py-2 text-sm">{formatPrice(item.unit_cost)}</td>
                                <td className="px-3 py-2 text-sm">{item.tax_percent}%</td>
                                <td className="px-3 py-2 text-sm font-medium">{formatPrice(lineTotalWithTax)}</td>
                                <td className="px-3 py-2 text-sm">
                                  <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Totals */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Other Charges</label>
                  <input
                    type="number"
                    name="other_charges"
                    value={formData.other_charges}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Total</label>
                  <input
                    type="number"
                    name="discount_total"
                    value={formData.discount_total}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">{formatPrice(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Tax Total:</span>
                  <span className="text-sm font-medium">{formatPrice(totals.taxTotal)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Other Charges:</span>
                  <span className="text-sm font-medium">{formatPrice(totals.otherCharges)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Discount:</span>
                  <span className="text-sm font-medium text-red-600">-{formatPrice(totals.discount)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-lg font-bold text-gray-900">Grand Total:</span>
                  <span className="text-lg font-bold text-gray-900">{formatPrice(totals.grandTotal)}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleFormChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {showDetails && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                Invoice #{selectedInvoice.purchase_invoice_no}
              </h2>
              <button onClick={() => setShowDetails(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Supplier</p>
                  <p className="text-lg font-semibold">{selectedInvoice.supplier_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Invoice Date</p>
                  <p className="text-lg font-semibold">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    selectedInvoice.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Grand Total</p>
                  <p className="text-lg font-bold">{formatPrice(selectedInvoice.grand_total)}</p>
                </div>
              </div>

              {/* Items */}
              {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Items</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-[720px] w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Unit Cost</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tax %</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Line Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{item.product_name || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm">{item.qty}</td>
                            <td className="px-4 py-2 text-sm">{formatPrice(item.unit_cost)}</td>
                            <td className="px-4 py-2 text-sm">{item.tax_percent || 0}%</td>
                            <td className="px-4 py-2 text-sm font-medium text-right">{formatPrice(item.line_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payments */}
              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Payments</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-[720px] w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Mode</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Reference</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedInvoice.payments.map((payment, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm">{formatDate(payment.payment_date)}</td>
                            <td className="px-4 py-2 text-sm capitalize">{payment.payment_mode}</td>
                            <td className="px-4 py-2 text-sm font-medium text-right">{formatPrice(payment.amount)}</td>
                            <td className="px-4 py-2 text-sm">{payment.txn_reference || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Grand Total:</span>
                  <span className="text-sm font-medium">{formatPrice(selectedInvoice.grand_total)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Paid Amount:</span>
                  <span className="text-sm font-medium text-green-600">{formatPrice(selectedInvoice.paid_amount || 0)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-lg font-bold text-gray-900">Balance Due:</span>
                  <span className={`text-lg font-bold ${
                    (parseFloat(selectedInvoice.grand_total) - parseFloat(selectedInvoice.paid_amount || 0)) === 0
                      ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatPrice(parseFloat(selectedInvoice.grand_total) - parseFloat(selectedInvoice.paid_amount || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Add Payment</h2>
              <button onClick={() => setShowPayment(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                  min="0"
                  step="0.01"
                  max={parseFloat(selectedInvoice.grand_total) - parseFloat(selectedInvoice.paid_amount || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Balance: {formatPrice(parseFloat(selectedInvoice.grand_total) - parseFloat(selectedInvoice.paid_amount || 0))}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  name="payment_date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
                <select
                  name="payment_mode"
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_mode: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                <input
                  type="text"
                  name="txn_reference"
                  value={paymentData.txn_reference}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, txn_reference: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPayment(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPurchaseInvoices;





